import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Zap, RefreshCw, ChevronDown, ChevronRight, Check, X } from "lucide-react";
import { toast } from "sonner";
import { fmtLog } from "@/lib/tz";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ACTION = {
  would_auto_approve: { label: "Shadow: would approve", c: "text-[#2f7d54] bg-emerald-50 border-emerald-200" },
  auto_approved: { label: "Auto-approved", c: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  blocked: { label: "Blocked", c: "text-amber-700 bg-amber-50 border-amber-200" },
};

const fmt = (dt) => fmtLog(dt);

const Block = ({ title, children }) => (
  <div className="mt-3">
    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{title}</div>
    {children}
  </div>
);
const Json = ({ value }) => (
  <pre className="mt-1 max-h-72 overflow-auto rounded-md bg-slate-50 border border-slate-100 p-2 text-[10px] whitespace-pre-wrap break-words">{JSON.stringify(value, null, 2)}</pre>
);

// One decision with what the AI saw: kept so a decision can be explained later
// (for example if a worker disputes their pay). Includes shift notes.
function DecisionDetail({ name, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!name) return;
    setD(null);
    api.aiApprovalLogEntry(name).then(setD).catch((e) => {
      toast.error(e.response?.data?.detail || "Could not load this decision");
      onClose();
    });
  }, [name]); // eslint-disable-line
  return (
    <Dialog open={!!name} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-decision-detail">
        <DialogHeader><DialogTitle className="text-base">What the AI saw</DialogTitle></DialogHeader>
        {!d ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
          <div className="text-xs text-slate-600">
            <div>{d.shift} · {d.worker_name} · {d.participant_name}</div>
            <div className="text-[11px] text-slate-400">
              {fmt(d.logged_at)} · {d.mode}{d.trigger ? ` · ${d.trigger}` : ""} · {d.model}
              {d.min_confidence != null ? ` · threshold ${d.min_confidence}%` : ""}{d.app_version ? ` · app ${d.app_version}` : ""}
            </div>
            {!d.inputs ? (
              <p className="mt-3 text-slate-500">This decision was logged before the app kept what the AI saw.</p>
            ) : (
              <>
                <Block title="AI's answer"><Json value={d.output} /></Block>
                <Block title="Facts sent to the AI"><Json value={d.inputs} /></Block>
                <Block title="Instructions">
                  <details className="mt-1"><summary className="cursor-pointer text-[11px] text-slate-500">Show the instructions the AI was given</summary>
                    <pre className="mt-1 max-h-72 overflow-auto rounded-md bg-slate-50 border border-slate-100 p-2 text-[10px] whitespace-pre-wrap">{d.prompt}</pre>
                  </details>
                </Block>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AiRolloutReport() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setBusy(true);
    try { setData(await api.aiApprovalLog()); }
    catch { toast.error("Could not load the AI rollout report"); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (open && !data) load(); }, [open]); // eslint-disable-line

  const s = data?.summary;
  const entries = data?.entries || [];

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3" data-testid="ai-rollout">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between" data-testid="ai-rollout-toggle">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
          <Zap className="h-3 w-3" />AI auto-approve activity
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>
      <p className="text-[11px] text-slate-500 mt-1">Every AI auto-approve decision (shadow would-approve, live auto-approve, or blocked) with the shift's current human status — so you can confirm the AI agrees with your reviewers before trusting live mode.</p>

      {open && (
        <div className="mt-3">
          <div className="flex justify-end mb-2">
            <button onClick={load} disabled={busy} className="text-xs text-[#3f9963] hover:text-emerald-800 flex items-center gap-1 font-semibold" data-testid="ai-rollout-refresh">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}Refresh
            </button>
          </div>

          {s && (
            <div className="grid grid-cols-4 gap-2 mb-3" data-testid="ai-rollout-summary">
              <Stat label="Would approve" value={s.would_auto_approve} tone="indigo" />
              <Stat label="Auto-approved" value={s.auto_approved} tone="emerald" />
              <Stat label="Blocked" value={s.blocked} tone="amber" />
              <Stat label="Shadow agreement" value={s.shadow_agreement_pct == null ? "—" : `${s.shadow_agreement_pct}%`}
                sub={s.would_auto_approve ? `${s.shadow_agreed}/${s.would_auto_approve} approved` : "no data"} tone="slate" />
            </div>
          )}

          {entries.length === 0 ? (
            <div className="text-xs text-slate-400 py-6 text-center" data-testid="ai-rollout-empty">
              {busy ? "Loading…" : "No AI auto-approve activity yet. Turn on shadow or live mode and run an AI review."}
            </div>
          ) : (
            <div className="border border-slate-100 rounded-md overflow-hidden" data-testid="ai-rollout-list">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[9px]">
                  <tr>
                    <th className="text-left px-2 py-1.5">Shift</th>
                    <th className="text-left px-2 py-1.5">Worker · Participant</th>
                    <th className="text-left px-2 py-1.5">Mode</th>
                    <th className="text-left px-2 py-1.5">AI decision</th>
                    <th className="text-left px-2 py-1.5">Conf</th>
                    <th className="text-left px-2 py-1.5">Human status</th>
                    <th className="text-left px-2 py-1.5">When</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const a = ACTION[e.action] || { label: e.action, c: "text-slate-600 bg-slate-50 border-slate-200" };
                    const agree = e.action === "would_auto_approve";
                    return (
                      <tr key={e.name} className="border-t border-slate-100" data-testid={`ai-rollout-row-${e.shift}`}>
                        <td className="px-2 py-1.5 font-mono text-slate-500">{e.shift}<div className="text-[9px] text-slate-400">{e.shift_date}</div></td>
                        <td className="px-2 py-1.5"><div className="text-slate-700">{e.worker_name || "—"}</div><div className="text-[9px] text-slate-400">{e.participant_name}</div></td>
                        <td className="px-2 py-1.5"><span className="capitalize text-slate-600">{e.mode}</span>{e.trigger ? <div className="text-[9px] text-slate-400">{e.trigger}</div> : null}</td>
                        <td className="px-2 py-1.5">
                          <span className={`inline-flex items-center gap-1 font-bold px-1.5 py-0.5 rounded-full border text-[10px] ${a.c}`}>{a.label}</span>
                          {e.action === "blocked" && e.reason ? <div className="text-[9px] text-amber-600 mt-0.5">{e.reason}</div> : null}
                        </td>
                        <td className="px-2 py-1.5 tabular-nums text-slate-600">{e.confidence != null ? `${e.confidence}%` : "—"}</td>
                        <td className="px-2 py-1.5">
                          <span className="text-slate-700">{e.current_status || "—"}</span>
                          {agree ? (
                            e.human_approved
                              ? <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-600 font-semibold" title="Human agreed"><Check className="h-3 w-3" />match</span>
                              : <span className="ml-1 inline-flex items-center gap-0.5 text-slate-400" title="Not yet approved by a human"><X className="h-3 w-3" />pending</span>
                          ) : null}
                        </td>
                        <td className="px-2 py-1.5 text-slate-400">{fmt(e.logged_at)}
                          <button onClick={() => setDetail(e.name)} data-testid={`ai-rollout-detail-${e.name}`}
                            className="block text-[10px] text-[#bf3b4f] hover:underline">What the AI saw</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <DecisionDetail name={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

const TONE = {
  indigo: "text-[#2f7d54] bg-emerald-50 border-emerald-100",
  emerald: "text-emerald-700 bg-emerald-50 border-emerald-100",
  amber: "text-amber-700 bg-amber-50 border-amber-100",
  slate: "text-slate-700 bg-slate-50 border-slate-100",
};

function Stat({ label, value, sub, tone }) {
  return (
    <div className={`rounded-md border p-2 ${TONE[tone] || TONE.slate}`}>
      <div className="text-[9px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-bold leading-tight">{value}</div>
      {sub ? <div className="text-[9px] opacity-60">{sub}</div> : null}
    </div>
  );
}
